import {
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    TooltipProps,
    ResponsiveContainer,
    LabelList
} from 'recharts';
import { amber, green, red } from '@mui/material/colors';
import { Box, Divider, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UcMultiSectionProgressBar from '../common/UcMultiSectionProgressBar';
import { useTheme } from '@mui/material/styles';
import { ChartInstanceOfMuiTheme } from '../readings/CustomerSummaryChartUtils';

// @ts-ignore
import { dateRangeFormatterIgnoreUtc } from 'lib/Formatters';
// @ts-ignore
import { useResponsive } from '../../lib/useResponsive';

const threshold = 15;    // Only show labels for values greater than this
const minBarWidthForLabel = 30; // Minimum bar width to display the label inside

// CustomLabel component to handle label positioning based on bar size
const CustomLabel = ({ x, y, width, height, value, formatter }: any) => {
    if (value > threshold) {
        // If the bar is too small, display the label outside
        const isBarTooSmall = height < minBarWidthForLabel;

        return (
            <text
                x={x + width / 2} // If too small, place label outside the bar
                y={y + height / 2}
                fill={isBarTooSmall ? 'black' : 'white'} // Change text color if it's outside the bar
                textAnchor={isBarTooSmall ? 'start' : 'middle'}
                dominantBaseline="middle"
                fontSize={18}
            >
                {isBarTooSmall ? <></> : formatter(value)}
            </text>
        );
    }
    return null;
};

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    if (active && payload?.length) {
        const { uomDisplay } = payload[0].payload;

        return (
            <List
                sx={{
                    width: '100%',
                    minWidth: '250px',
                    bgcolor: 'background.paper',
                    border: '1px solid #cccccc',
                    borderRadius: '5px'
                }}>
                <ListItem>
                    <Typography variant="subtitle1">
                        {label}
                    </Typography>
                </ListItem>
                <Divider />
                {label.toUpperCase() === 'YOUR USAGE' ? (
                    <ListItem sx={{ pb: 0 }}>
                        <ListItemText
                            primary={`${payload[0].payload['Usage']} ${uomDisplay}`}
                            secondary={'Total'}
                        />
                    </ListItem>
                ) : (
                    <>
                        <ListItem sx={{ pb: 0 }}>
                            <ListItemText
                                primary={`${payload[0].payload['Avg. Usage']} ${uomDisplay}`}
                                secondary={'Avg.'}
                            />
                        </ListItem>
                        <ListItem sx={{ pb: 0 }}>
                            <ListItemText
                                primary={`${payload[0].payload['Max. Usage']} ${uomDisplay}`}
                                secondary={'Max.'}
                            />
                        </ListItem>
                        <ListItem sx={{ pt: 0 }}>
                            <ListItemText
                                primary={`${payload[0].payload['Min. Usage']} ${uomDisplay}`}
                                secondary={'Min.'}
                            />
                        </ListItem>
                    </>
                )}
            </List >
        );
    }

    return null;
};

interface NeighborhoodCompareWidgetChartProps {
    dataWarehouse: any;
    currentBillingCycle: any;
    uom: string
    uomDisplay: string
}

function NeighborhoodCompareWidgetChart(props: Readonly<NeighborhoodCompareWidgetChartProps>) {
    const { t } = useTranslation();
    const { isDesktop } = useResponsive();
    const { dataWarehouse, currentBillingCycle, uom, uomDisplay } = props;
    const theme = useTheme<ChartInstanceOfMuiTheme>();

    const data = [
        {
            name: `Your Usage`,
            Usage: dataWarehouse?.result?.myTotalUsage?.toFixed(2),
            uomDisplay: uomDisplay
        },
        {
            name: 'Neighborhood \nUsage',
            'Avg. Usage': dataWarehouse?.result?.avgTotalUsage?.toFixed(2),
            'Min. Usage': Math.max(0, dataWarehouse?.result?.minTotalUsage?.toFixed(2)),
            'Max. Usage': dataWarehouse?.result?.maxTotalUsage?.toFixed(2),
            uomDisplay: uomDisplay
        },
    ];

    const sections = [
        { label: 'Above Avg. Usage', percentage: 25, color: red[400] },
        { label: 'Avg. Usage', percentage: 50, color: amber[300] },
        { label: 'Below  Avg. Usage', percentage: 25, color: green[400] },
    ];

    const labelFormatter = (value: any) => {
        return parseInt(value) + ` ${uomDisplay}`;
    }

    const fillColor = (shade: "light" | "main" | "dark") => {
        const uomKey = uom?.toLowerCase();

        return theme?.props?.Commodity?.[uomKey]?.[shade] ?? "#3db38c";
    }

    return (
        <>
            <div className="dataCyId" data-cy-id="neighborhood_comparison_dashboard_widget" />
            <Box sx={{ p: "1rem", position: 'relative', height: '100%' }}>
                <Typography variant={'subtitle1'} align="center" sx={{ pt: 2, fontWeight: 'bold' }}>
                    {t('Comparison for Previous Period ({{dateRange}})', { dateRange: dateRangeFormatterIgnoreUtc(currentBillingCycle?.startDate, currentBillingCycle?.endDate, true) })}
                </Typography>

                <ResponsiveContainer
                    width="100%"
                    height="100%"
                    maxHeight={isDesktop ? 275 : 250}
                >
                    <ComposedChart
                        data={data}
                        width={300}
                        height={300}
                        margin={{
                            top: 10,
                            right: 20,
                            bottom: 30,
                            left: 20,
                        }}
                    >
                        <CartesianGrid stroke="#f5f5f5" />
                        <XAxis dataKey="name" />
                        <YAxis hide={true} />
                        <Tooltip content={CustomTooltip} />
                        <Bar dataKey="Usage" stackId="a" barSize={200} fill={fillColor('main')} radius={[8, 8, 0, 0]}>
                            <LabelList dataKey="Usage" content={CustomLabel} formatter={labelFormatter} />
                        </Bar>
                        <Bar dataKey="Avg. Usage" stackId="a" barSize={200} fill={fillColor('dark')} radius={[8, 8, 0, 0]}>
                            <LabelList dataKey="Avg. Usage" content={CustomLabel} formatter={labelFormatter} />
                        </Bar>
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
            <UcMultiSectionProgressBar sections={sections} indicatorValue={dataWarehouse?.result?.percentile} />
        </>
    );
}

export default NeighborhoodCompareWidgetChart;